import type { FeedItem } from '@knocklabs/client';
import { RiArrowRightLine, RiCheckLine, RiErrorWarningLine } from '@remixicon/react';
import { NotificationType } from '@shared/types';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { navigateHref } from 'src/lib/routing';
import { appLinks } from 'src/routing/appLinks';

import { relativeTime } from './notificationCell.helpers';

/** Loosely-typed view of ZatannaSyncFailedPayload (Knock feed data is untyped). */
interface ZatannaSyncFailedData {
  type?: string;
  order_slug?: string;
  order_path?: string;
  thread_id?: number;
  line_item?: string;
  error?: string;
}

/** True when this notification is a Zatanna (iSolve/Famous) sync failure. */
export function isZatannaSyncFailedNotification(data: { type?: string } | null): boolean {
  return data?.type === NotificationType.ZATANNA_SYNC_FAILED;
}

interface ZatannaSyncFailedCellProps {
  item: FeedItem;
  onMarkAsRead?: () => void;
}

export default function ZatannaSyncFailedCell({ item, onMarkAsRead }: ZatannaSyncFailedCellProps) {
  const navigate = useNavigate();
  const router = useRouter();
  const data = (item.data ?? {}) as ZatannaSyncFailedData;
  const isRead = !!item.read_at;

  // Deep-link rule (TAS-3172): the order if it exists, else the originating
  // thread, else the sales-orders list (so the click is never a dead no-op).
  const handleClick = () => {
    if (data.order_path) {
      void navigateHref(router, data.order_path);
    } else if (data.thread_id != null) {
      void navigate(appLinks.aiInboxThread({ threadId: String(data.thread_id) }));
    } else {
      void navigate(appLinks.salesOrders());
    }
  };

  const title = data.order_slug ?? 'Order';

  return (
    <div
      onClick={handleClick}
      className="group relative flex flex-row items-start px-5 py-4 gap-3 cursor-pointer hover:bg-neutral-2"
    >
      {/* Unread dot */}
      {!isRead && <span className="absolute left-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-green-600" />}

      {/* Icon badge — integration failure (red) */}
      <div className="flex items-center justify-center w-8 h-8 rounded-full border border-red-border bg-red-bg shrink-0">
        <RiErrorWarningLine size={16} className="text-red-icon" />
      </div>

      {/* Content */}
      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
        {/* Row 1: order slug + headline */}
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-14 font-medium leading-5 tracking-[-0.1px] text-gray-900 truncate">{title}</span>
          <span className="text-14 leading-5 tracking-[-0.1px] text-neutral-alpha-9 shrink-0">sync failed</span>
        </div>

        {/* Row 2: sanitized error (+ line-item context for partial failures) */}
        <div className="flex items-start gap-1">
          <span className="text-12 leading-4 font-medium text-red-text line-clamp-2">
            {data.line_item ? `${data.line_item}: ` : ''}
            {data.error ?? 'Could not sync to the ERP.'}
          </span>
        </div>
      </div>

      {/* Right column: time + actions */}
      <div className="flex flex-col items-end justify-between gap-2 shrink-0">
        <span className="text-12 leading-4 text-neutral-alpha-9 text-right">{relativeTime(item.inserted_at)}</span>
        <div className="flex items-center gap-1">
          {!isRead && onMarkAsRead && (
            <button
              title="Mark as read"
              onClick={(e) => {
                e.stopPropagation();
                onMarkAsRead();
              }}
              className="hidden group-hover:flex items-center justify-center w-6 h-6 rounded-lg hover:bg-black/8 text-neutral-alpha-9 hover:text-gray-900"
            >
              <RiCheckLine size={14} />
            </button>
          )}
          <button className="flex items-center justify-center px-2 h-6 gap-1 rounded-lg bg-black/4">
            <span className="text-12 font-medium text-gray-900">View order</span>
            <RiArrowRightLine size={14} className="text-neutral-alpha-9" />
          </button>
        </div>
      </div>
    </div>
  );
}
